export type History<T = any> = {
  value: T;
  canUndo: boolean;
  canRedo: boolean;
  undo(): void;
  redo(): void;
  jump(value: T): void;
  jumpIndex(i: number): void;
  changes(): T[];
};

type HistoryOptions<T = any> = {
  value?: T;
  undoStack?: T[];
  cloneValue?: boolean;
  onNavigate?: (value: T) => void;
};

export class StateHistory<T = any> implements History<T> {
  private undoStack?: T[];
  private redoStack?: T[];
  private cloneValue?: boolean;

  private _current: T;

  private onNavigate: (value: T) => void;

  /**
   * Creates a new instance with an initial value
   * @param value Initial value
   * @param cloneValue If true non-primitive values will be deep-cloned whenever the value is set
   */
  constructor(opt?: HistoryOptions) {
    const { value, undoStack, cloneValue, onNavigate } = {
      ...{ cloneValue: true },
      ...opt,
    };

    this.cloneValue = cloneValue || false;
    this.undoStack = undoStack || [];
    this.redoStack = [];

    if (value !== undefined) {
      this.setValue(value);
    }

    this.onNavigate = onNavigate;
  }
  /**
   * Gets the current value
   */
  get value() {
    return this._current;
  }
  /**
   * Sets the current value and pushes the old value onto the undo stack
   */
  set value(v: T) {
    this.setValue(v);
  }
  /**
   * Sets the current value and pushes the old value onto the undo stack
   * @param value New value
   */
  setValue(value: T) {
    if (this._current !== undefined) {
      this.undoStack.push(this._current);
    }
    if (
      this.cloneValue &&
      ['string', 'number', 'boolean'].indexOf(typeof value) < 0
    ) {
      this._current = JSON.parse(JSON.stringify(value));
    } else {
      this._current = value;
    }
    // Invalidate redo
    this.redoStack = [];
  }
  /**
   * Sets the value to the previous value and returns it
   */
  undo(event = true): T {
    if (this.undoStack.length > 0) {
      if (this._current !== undefined) {
        this.redoStack.push(this._current);
      }
      this._current = this.undoStack.pop();
    } else {
      this._current = undefined;
    }
    if (event) {
      if (this.onNavigate) {
        this.onNavigate(this._current);
      }
    }
    return this._current;
  }
  /**
   * Sets the value back to the value before undo was called
   */
  redo(event = true): T {
    if (this.redoStack.length > 0) {
      if (this._current !== undefined) {
        this.undoStack.push(this._current);
      }
      this._current = this.redoStack.pop();
    } else {
      this._current = undefined;
    }
    if (event) {
      if (this.onNavigate) {
        this.onNavigate(this._current);
      }
    }
    return this._current;
  }
  /**
   * Returns true if the value can be undone
   */
  get canUndo() {
    return this.undoStack.length > 0;
  }
  /**
   * Returns true if the value can be redone
   */
  get canRedo() {
    return this.redoStack.length > 0;
  }

  //TODO requires strict equality, consider predicate config
  jump(value: T) {
    let ref: T = this._current;

    let i = this.undoStack.indexOf(value);
    if (i > -1) {
      while (ref != value) {
        ref = this.undo(false);
      }
    } else {
      i = this.redoStack.indexOf(value);
      if (i > -1) {
        while (ref != value) {
          ref = this.redo(false);
        }
      }
    }
    if (this.onNavigate) {
      this.onNavigate(this._current);
    }
  }
  jumpIndex(i: number) {
    const stack = this.undoStack.concat(this.redoStack);
    if (stack[i]) {
      this.jump(stack[i]);
    }
  }
  changes(): T[] {
    return this.undoStack.concat(this.redoStack);
  }
}
